import classNames from 'classnames';
import React, { Children, ReactNode, useEffect, useRef, useState } from 'react';
import style from './ScrollComponent.module.scss';
import { useScrollDirection } from './hooks/useScrollDirection';

type ScrollComponentProps = {
	children: ReactNode[];
	activeElement?: number;
	delay?: number; //Alter the scrolling length before scrolling occurs
	targetClass?: string; //Alternative class where overflow will occur
	onIndexChange?: (newIndex: number, child?: ReactNode) => void; // Callback for index change
	disableScroll?: boolean;
};

export const ScrollComponent: React.FC<ScrollComponentProps> = ({
	children,
	activeElement = 0,
	delay,
	targetClass,
	onIndexChange,
	disableScroll = false,
}) => {
	const concatTargetClass = targetClass ? `${style.show} ${targetClass}` : style.show;
	const scrollComponentRef = useRef(null);
	const { state } = useScrollDirection({ element: scrollComponentRef, targetClass: concatTargetClass, delay: delay });
	const [index, setIndex] = useState(activeElement);

	useEffect(() => {
		setIndex(activeElement);
	}, [activeElement]);

	useEffect(() => {
		if (disableScroll) return;

		const scrollToIndex = (targetIndex: number, direction: 'UP' | 'DOWN') => {
			setIndex(targetIndex);
			onIndexChange?.(targetIndex, children[targetIndex]);

			requestAnimationFrame(() => {
				const wrapper = sectionRefs.current[targetIndex] as HTMLElement | null;
				const scrollable = wrapper?.querySelector('[data-scrollable="true"]') as HTMLElement | null;

				if (scrollable) {
					scrollable.scrollTop = 0;
				} else {
					console.warn(`[${direction}] Scrollable section not found at index:`, targetIndex);
				}
			});
		};

		switch (true) {
			case state.direction === 'DOWN' && index < children.length - 1: {
				scrollToIndex(index + 1, 'DOWN');
				break;
			}
			case state.direction === 'UP' && index > 0: {
				scrollToIndex(index - 1, 'UP');
				break;
			}
			default:
				console.warn('Unexpected scroll state or boundaries hit:', {
					direction: state.direction,
					index,
					childrenLength: children.length,
				});
		}
	}, [state]);

	const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

	const mapContent = () =>
		Children.map(children, (child, i) => {
			return (
				<div
					ref={(el) => {
						sectionRefs.current[i] = el;
					}}
					className={classNames(
						style.ScrollComponent__animate,
						i === index ? style.show : i < index ? style.hideUp : style.hideDown
					)}
				>
					{child}
				</div>
			);
		});

	return (
		<div
			ref={scrollComponentRef}
			className={style.ScrollComponent}
		>
			{mapContent()}
		</div>
	);
};
